import { useMemo } from 'react'

import { useLocation } from 'react-router'

type ParamMap = Record<string, string>

const isParamMap = (arg: unknown): arg is ParamMap => {
  return typeof arg === 'object' && arg !== null && !Array.isArray(arg)
}

// Variadic form — result keys match the query param names: createQueryParams('foo', 'bar')
function createQueryParams<T extends string>(...keys: T[]): () => { [K in T]?: string }
// Map form — result keys are renamed from the query param names: createQueryParams({ clientId: 'client_id' })
function createQueryParams<M extends ParamMap>(mapping: M): () => { [K in keyof M]?: string }
function createQueryParams(...args: [ParamMap] | string[]) {
  let entries: [string, string][]
  if (args.length === 1 && isParamMap(args[0])) {
    entries = Object.entries(args[0])
  } else if (args.every((arg) => typeof arg === 'string')) {
    entries = args.map((key) => [key, key])
  }

  const useQueryParams = () => {
    const location = useLocation()
    return useMemo(() => {
      const params = new URLSearchParams(location.search)
      return entries.reduce(
        (result, [key, paramName]) => {
          const value = params.get(paramName)
          if (value !== null) {
            result[key] = value
          }
          return result
        },
        {} as Record<string, string>,
      )
    }, [location.search])
  }
  return useQueryParams
}

export default createQueryParams
